import { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Typography,
  Unstable_Grid2 as Grid,
  List,
  ListItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventIcon from "@mui/icons-material/Event";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

import ViewContent from "../../../components/ViewContent";
import Modal from "../../../components/Modal";
import NewReserveForm from "./NewReserveForm";
import { useModal } from "../../../contexts/ModalContext";
import { useRoomReservations } from "../../../hooks/useRoomReservations";
import { RoomReservation, CancelDialogState, User } from "../../../types/rooms";
import { fetchData } from "../../../fetchData";

// Constantes inline
const DAYS_SHORT = ["D", "S", "T", "Q", "Q", "S", "S"];

const Rooms = () => {
  const modal = useModal();
  const [current, setCurrent] = useState(() => new Date());
  const [selected, setSelected] = useState<Date | null>(new Date());
  const [backdrop, setBackdrop] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [cancelDialog, setCancelDialog] = useState<CancelDialogState>({
    open: false,
    reservation: null,
  });

  const { reservations, loading, loadReservations, deleteReservation } =
    useRoomReservations();

  useEffect(() => {
    loadReservations();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const result = await fetchData("/users?is_admin=true&status=true", {
        method: "GET",
      });

      if (result && result.rows) {
        setUsers(Array.isArray(result.rows) ? result.rows : []);
      } else {
        const fallbackResult = await fetchData("/runrunit/users", {
          method: "GET",
        });
        setUsers(Array.isArray(fallbackResult) ? fallbackResult : []);
      }
    } catch (e) {
      console.error("Erro ao carregar usuários:", e);
      try {
        const fallbackResult = await fetchData("/runrunit/users", {
          method: "GET",
        });
        setUsers(Array.isArray(fallbackResult) ? fallbackResult : []);
      } catch (fallbackError) {
        console.error("Erro no fallback também:", fallbackError);
        setUsers([]);
      }
    }
  };

  const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);

  const daysArray = useMemo(() => {
    const end = new Date(current.getFullYear(), current.getMonth() + 1, 0);
    const days: Date[] = [];
    for (let d = 1; d <= end.getDate(); d++) {
      days.push(new Date(current.getFullYear(), current.getMonth(), d));
    }
    return days;
  }, [current]);

  const handlePrevMonth = () => {
    setCurrent(new Date(current.getFullYear(), current.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrent(new Date(current.getFullYear(), current.getMonth() + 1, 1));
  };

  const handlePrevYear = () => {
    setCurrent(new Date(current.getFullYear() - 1, current.getMonth(), 1));
  };

  const handleNextYear = () => {
    setCurrent(new Date(current.getFullYear() + 1, current.getMonth(), 1));
  };

  const handleNew = () => {
    modal?.open(
      "Nova Reserva",
      <NewReserveForm
        onClose={modal?.close}
        initialDate={selected ?? undefined}
        onSuccess={loadReservations}
      />,
    );
  };

  const handleCancelReservation = (reservation: RoomReservation) => {
    setCancelDialog({ open: true, reservation });
  };

  const confirmCancelReservation = async () => {
    if (!cancelDialog.reservation) return;

    setBackdrop(true);

    try {
      const success = await deleteReservation(cancelDialog.reservation._id);
      if (success) {
        setCancelDialog({ open: false, reservation: null });
        loadReservations();
      }
    } finally {
      setBackdrop(false);
    }
  };

  const handleViewReservation = (reservation: RoomReservation) => {
    modal?.open(
      "Visualizar Reserva",
      <NewReserveForm
        onClose={modal?.close}
        initialDate={new Date(reservation.startTime)}
        onSuccess={loadReservations}
        editData={reservation}
        viewOnly={true}
      />,
    );
  };

  const handleEditReservation = (reservation: RoomReservation) => {
    modal?.open(
      "Editar Reserva",
      <NewReserveForm
        onClose={modal?.close}
        initialDate={new Date(reservation.startTime)}
        onSuccess={loadReservations}
        editData={reservation}
      />,
    );
  };

  const sameDay = (a?: Date | null, b?: Date | null) => {
    if (!a || !b) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  const getUserNameByLogin = (login: string) => {
    const user = users.find((u) => u.login === login);
    return user?.name || login;
  };

  const getFirstTwoNames = (login: string) => {
    const fullName = getUserNameByLogin(login);
    const names = fullName.trim().split(" ");
    return names.slice(0, 2).join(" ");
  };

  const selectedReservations = reservations.filter((r) => {
    const d = new Date(r.startTime);
    const isDeleted = r.edit?.some((edit: any) => edit.action === "delete");
    return selected && sameDay(d, selected) && !isDeleted;
  });

  return (
    <ViewContent
      title="Reservar Sala de Reunião"
      icon={<EventIcon sx={{ mr: 1 }} />}
    >
      <Grid container spacing={3}>
        <Grid xs={12} lg={7}>
          <Card>
            <CardHeader
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1">Calendário</Typography>
                </Stack>
              }
              action={
                <Stack direction="row" spacing={2} alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton onClick={handlePrevYear} size="small">
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography
                      variant="subtitle2"
                      sx={{ minWidth: 60, textAlign: "center" }}
                    >
                      {current.getFullYear()}
                    </Typography>
                    <IconButton onClick={handleNextYear} size="small">
                      <ChevronRightIcon />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton onClick={handlePrevMonth} size="small">
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography
                      variant="subtitle2"
                      sx={{ minWidth: 80, textAlign: "center" }}
                    >
                      {current.toLocaleDateString("pt-BR", { month: "long" })}
                    </Typography>
                    <IconButton onClick={handleNextMonth} size="small">
                      <ChevronRightIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              }
            />
            <CardContent>
              <Stack spacing={2}>
                <Grid container columns={7} spacing={0}>
                  {DAYS_SHORT.map((d, index) => (
                    <Grid key={`day-${index}`} xs={1}>
                      <Typography variant="caption" color="text.secondary">
                        {d}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
                <Grid container columns={7} spacing={1}>
                  {Array.from({ length: monthStart.getDay() || 0 }).map(
                    (_, idx) => (
                      <Grid key={`e-${idx}`} xs={1}>
                        <Box sx={{ height: 36 }} />
                      </Grid>
                    ),
                  )}
                  {daysArray.map((d) => {
                    const isSelected = selected && sameDay(d, selected);
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    return (
                      <Grid key={d.toISOString()} xs={1}>
                        <Button
                          variant={isSelected ? "contained" : "text"}
                          onClick={() => {
                            if (!isWeekend) {
                              setSelected(d);
                              loadReservations(d);
                            }
                          }}
                          size="small"
                          disabled={isWeekend}
                          sx={{
                            width: 45,
                            height: 45,
                            minWidth: 0,
                            borderRadius: 1,
                            opacity: isWeekend ? 0.7 : 1,
                          }}
                        >
                          {d.getDate()}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} lg={5}>
          <Card>
            <CardHeader
              title={
                <Typography variant="subtitle1">Lista de Reservas</Typography>
              }
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNew}
                >
                  Nova Reserva
                </Button>
              </Box>
              <List sx={{ p: 0 }}>
                {selectedReservations.map((r) => (
                  <ListItem
                    key={r._id}
                    sx={{
                      mb: 1,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      flexDirection: "column",
                      alignItems: "stretch",
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.reason}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(r.startTime).toLocaleDateString("pt-BR", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          })}{" "}
                          {` - ${new Date(r.startTime).toLocaleTimeString(
                            "pt-BR",
                            { hour: "2-digit", minute: "2-digit" },
                          )} às ${new Date(r.endTime).toLocaleTimeString(
                            "pt-BR",
                            { hour: "2-digit", minute: "2-digit" },
                          )}`}
                          {r.user && <span> • {getFirstTwoNames(r.user)}</span>}
                        </Typography>
                      </Box>
                      <Chip
                        label={r.isClient ? "CLIENTE" : "INTERNO"}
                        size="small"
                        color={r.isClient ? "error" : "primary"}
                        variant="filled"
                        sx={{ ml: 1, flexShrink: 0 }}
                      />
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="space-between"
                    >
                      <Button
                        color="quinary"
                        variant="outlined"
                        size="small"
                        fullWidth
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewReservation(r)}
                      >
                        Visualizar
                      </Button>
                      <Button
                        color="primary"
                        variant="outlined"
                        size="small"
                        fullWidth
                        startIcon={<EditIcon />}
                        onClick={() => handleEditReservation(r)}
                      >
                        Editar
                      </Button>
                      <Button
                        color="error"
                        variant="outlined"
                        size="small"
                        fullWidth
                        startIcon={<DeleteIcon />}
                        onClick={() => handleCancelReservation(r)}
                      >
                        Cancelar
                      </Button>
                    </Stack>
                  </ListItem>
                ))}
                {selectedReservations.length === 0 && !loading && (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma reserva para o dia selecionado.
                  </Typography>
                )}
                {loading && (
                  <Typography variant="body2" color="text.secondary">
                    Carregando reservas...
                  </Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Modal />

      <Dialog
        open={cancelDialog.open}
        onClose={() => setCancelDialog({ open: false, reservation: null })}
      >
        <DialogTitle>Confirmar Cancelamento</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja cancelar a reserva</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelDialog({ open: false, reservation: null })}
            disabled={backdrop}
          >
            Não
          </Button>
          <Button
            onClick={confirmCancelReservation}
            color="error"
            disabled={backdrop}
          >
            {backdrop ? "Cancelando..." : "Sim, Cancelar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={backdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </ViewContent>
  );
};

export default Rooms;
